import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { TaskOrientation, TaskProps } from '../services/task';
import Loading from '../ui/Loading/Loading';
import { ProgressShape } from '../ui/Progress/Progress';
import { stores, useStore } from '../utils/store';
import Task from './Task/Task';

export default function Feed() {
    const orientation: TaskOrientation = TaskOrientation.VERTICAL;
    const [tasks,] = useStore(stores.tasks);
    const doneCount = tasks.filter(task => task.done).length;

    const loadMoreOlderRef = useRef(null);
    const loadMoreNewerRef = useRef(null);
    const updateRef: any = useRef();

    const tasksByDate = useMemo(() => {
        const today = (new Date).getFullYear() + "-" + ((new Date).getMonth() + 1) + "-" + (new Date).getDate();
        const hash: { [key: string]: TaskProps[] } = { [today]: [] };
        tasks.forEach(task => {
            const date = new Date(task.date);
            const dateDisplay: string = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();

            if (!hash[dateDisplay]) hash[dateDisplay] = [];
            hash[dateDisplay].push(task);
        })
        const dates = Object.keys(hash).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        return dates.map(date => ({ date: date, tasks: hash[date], isToday: date === today }));

    }, [tasks]);

    const [inited, setInited] = useState(false);

    const [visibleDates, setVisibleDates] = useState(initVisibleDatesIndexes);
    const visibleDatesIndexes = Array(visibleDates[1] - visibleDates[0] + 1).fill(0).map((e, i) => visibleDates[0] + i);
    useLayoutEffect(() => { !inited && setVisibleDates(initVisibleDatesIndexes()) }, [tasks])
    useLayoutEffect(() => {
        const interval = setInterval(manageProceduralLoading, 200); return () => clearInterval(interval);
    }, [])

    function manageProceduralLoading() {
        const rectNewer = (loadMoreNewerRef.current as any as HTMLElement).getBoundingClientRect();
        const rectOlder = (loadMoreOlderRef.current as any as HTMLElement).getBoundingClientRect();

        if (rectNewer.top <= window.innerHeight) updateRef.current.showMoreNewerTasks();
        // if (rectNewer.top >= window.innerHeight * 3) updateRef.current.showLessNewerTasks();
        if (rectOlder.bottom >= 0) updateRef.current.showMoreOlderTasks();
        // if (rectOlder.bottom <= -window.innerHeight * 2) updateRef.current.showLessOlderTasks();
    }

    function initVisibleDatesIndexes() {
        if (tasks.length) setInited(true);
        let indexOfToday = tasksByDate.findIndex(date => date.isToday);
        return [indexOfToday, indexOfToday + 1];
    }

    function showMoreOlderTasks() {
        let addedDateIndex = visibleDates[0] - 1;
        if (addedDateIndex < 0) return;
        setVisibleDates([addedDateIndex, visibleDates[1]])
        requestAnimationFrame(() => {
            const elementHeight = tasksByDate[addedDateIndex].tasks.length * (140 + 24) + 24 + 27 + (tasksByDate[addedDateIndex].tasks.length ? 0 : 24 + 74);
            window.scrollBy(0, elementHeight)
        });
    }

    function showLessOlderTasks() {
        let removedDateIndex = visibleDates[0];
        if (removedDateIndex >= tasksByDate.length || removedDateIndex >= visibleDates[1]) return;
        setVisibleDates([removedDateIndex + 1, visibleDates[1]])
        const currentScroll = window.scrollY;

        requestAnimationFrame(() => {
            const elementHeight = tasksByDate[removedDateIndex].tasks.length * (140 + 24) + 24 + 27 + (tasksByDate[removedDateIndex].tasks.length ? 0 : 24 + 74);
            window.scrollBy(0, -elementHeight)
            // window.scrollTo(0, window.scrollY - elementHeight);
        });
    }

    function showMoreNewerTasks() {
        setVisibleDates([visibleDates[0], Math.min(visibleDates[1] + 1, tasksByDate.length)])
    }

    function showLessNewerTasks() {
        setVisibleDates([visibleDates[0], Math.max(visibleDates[1] - 1, 0)])
    }

    updateRef.current = { showMoreOlderTasks, showMoreNewerTasks, showLessNewerTasks, showLessOlderTasks };

    function getProgressStyle() {
        return orientation === TaskOrientation.VERTICAL ? { height: doneCount * (140 + 24) + 40 } : { width: doneCount * (140 + 24) + 40 }
    }

    function getDateDisplayName(date: string) {
        const today = new Date();
        const taskDate = new Date(date);

        today.setHours(0); today.setMinutes(0); today.setSeconds(0); today.setMilliseconds(0);
        taskDate.setHours(0); taskDate.setMinutes(0); taskDate.setSeconds(0); taskDate.setMilliseconds(0);

        const difference = taskDate.getTime() - today.getTime();
        const differenceInDays = Math.ceil(difference / (1000 * 3600 * 24));

        if (differenceInDays === 0) return 'Today';
        if (differenceInDays === 1) return 'Tomorrow';
        return differenceInDays > 0 ? `In ${differenceInDays} days` : `${-differenceInDays} days ago`;
    }

    return (
        <Wrapper className="feed" orientation={orientation}>
            <div className='feed-timeline-container'>
                {/* <div className='feed-timeline-progress' style={getProgressStyle()}></div> */}
            </div>
            <div className='feed-content'>
                <div className='feed-load-more' ref={loadMoreOlderRef} style={{ height: 74 }}>
                    {visibleDates[0] === 0 ? <div className='feed-all-done'>Hi there. Good luck :)</div> : <Loading shape={ProgressShape.BAR}></Loading>}
                </div>
                {visibleDatesIndexes.map(index => {
                    let day = tasksByDate[index];
                    if (!day) return;
                    return (<div className='feed-content-day' key={day.date}>
                        <div className='feed-date'>{getDateDisplayName(day.date)}</div>
                        {day.tasks.map(task => <Task key={task.id} orientation={orientation} task={task}></Task>)}
                        {day.isToday && day.tasks.length === 0 && <div className='feed-load-more'><div className='feed-all-done'>No tasks for Today :)</div></div>}
                    </div>)
                })}
                <div className='feed-load-more' ref={loadMoreNewerRef} style={{ height: 74 }}>
                    {visibleDates[1] === tasksByDate.length ? <div className='feed-all-done'>No more</div> : <Loading shape={ProgressShape.BAR}></Loading>}
                </div>
                {/* <div style={{ height: 2000 }}></div> */}
                {/* {tasksByDate.map((day) => <div className='feed-content-day' key={day.date}>
                    <div className='feed-date'>{getDateDisplayName(day.date)}</div>
                    {day.tasks.map(task => <Task key={task.id} orientation={orientation} task={task}></Task>)}
                </div>)} */}
            </div>
        </Wrapper>

    )
}

const Wrapper = styled.div<{ orientation: TaskOrientation }>`
    width: 100%;
    display: flex;
    justify-content: center;

    .feed-date {
        font-size: 14px;
        padding: 10px 0 0 10px;
    }


    .feed-content {
        display: flex;
        justify-content: center;
        gap: 24px;
    }

    .feed-content-day {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .feed-load-more {
        padding: 10px 30px;
    }

    .feed-all-done {
        background-color: #262933;
        padding: 20px 10px;
        text-align: center;
        border-radius: ${props => props.theme.borderRadius};
        font-size: 13px;
        color: #cccccc;
    }

    .feed-timeline-container {
        background-color: #262933;
    }

    .feed-timeline-progress {
        background-color: #00796b;
    }
    

    ${({ orientation }) => orientations[orientation]}
`

const orientations = {
    [TaskOrientation.HORIZONTAL]: css`
        flex-direction: column-reverse;
        .feed-timeline-container {
            height: 4px;
            min-height: 4px;
            margin: 8px 0;
        }

        .feed-timeline-progress {
            height: 4px;
            width: 360px;
        }

        .feed-content {
            justify-content: unset;
            margin-right: auto;
            margin-left: auto;
            /* overflow-y: scroll; */
        }

        .feed-content-day {
            flex-direction: row;
        }

        .task-description {
            padding-right: 0;
            padding-left: 0;
        }
        
    `,
    [TaskOrientation.VERTICAL]: css`
        padding: 20px 10px;

        .feed-content {
            flex-direction: column;
            max-width: 500px;
        }

        .feed-timeline-container {
            width: 4px;
            min-width: 4px;
            margin: 0 8px;
        }

        .feed-timeline-progress {
            width: 4px;
            height: 360px;
        }
    `,
}